/**
 * SubscriptionPlansAdmin – Admin: Settings → Subscription Plans
 * List / Create / Edit / Delete plans + configure payment settings (bank/UPI/QR).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, Crown, Save, X, ChevronDown, ChevronUp,
  Building2, Upload, RefreshCw, Settings, ToggleLeft, ToggleRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import MediaUploadInput from "@/components/MediaUploadInput";

const API = process.env.REACT_APP_BACKEND_URL;

const BLANK_PLAN = {
  name: "", billing: "monthly", validity_days: 30, price: "", gst_type: "inclusive",
  gst_percent: 18, download_limit: 10, download_reset: "per_subscription",
  data_access: "complete", bulk_export: true, features: [],
  visibility: "public", status: "active", renewal: "manual", is_free_tier: false,
};

function Field({ label, value, onChange, type = "text", hint, children }) {
  const cls = "w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:border-orange-400 bg-white";
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children || (type === "select" ? null : (
        <input type={type} value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />
      ))}
      {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-slate-600">{label}</span>
      <button type="button" onClick={() => onChange(!value)}
        className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${value ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-500"}`}>
        {value ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
        {value ? "On" : "Off"}
      </button>
    </div>
  );
}

function PlanForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({ ...BLANK_PLAN, ...initial });
  const [featureInput, setFeatIn] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addFeature = () => {
    const t = featureInput.trim();
    if (!t) return;
    set("features", [...(form.features || []), t]);
    setFeatIn("");
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Plan Name" value={form.name} onChange={v => set("name", v)} />
        <Field label="Price (₹)" value={form.price} onChange={v => set("price", v)} type="number" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select label="Billing Cycle" value={form.billing} onChange={v => set("billing", v)}
          options={[["monthly","Monthly"],["quarterly","Quarterly"],["yearly","Yearly"],["lifetime","Lifetime"]]} />
        <Field label="Validity (days)" value={form.validity_days} onChange={v => set("validity_days", v)} type="number" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select label="GST Type" value={form.gst_type} onChange={v => set("gst_type", v)}
          options={[["inclusive","GST Inclusive"],["exclusive","GST Exclusive"]]} />
        <Field label="GST %" value={form.gst_percent} onChange={v => set("gst_percent", v)} type="number" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Download Limit (-1 = unlimited)" value={form.download_limit} onChange={v => set("download_limit", v)} type="number" />
        <Select label="Download Reset" value={form.download_reset} onChange={v => set("download_reset", v)}
          options={[["per_subscription","Per Subscription"],["monthly","Monthly"],["daily","Daily"]]} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select label="Data Access" value={form.data_access} onChange={v => set("data_access", v)}
          options={[["complete","Complete Product Data"],["basic","Basic Data Only"]]} />
        <Select label="Visibility" value={form.visibility} onChange={v => set("visibility", v)}
          options={[["public","Public"],["private","Private"],["hidden","Hidden"]]} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select label="Status" value={form.status} onChange={v => set("status", v)}
          options={[["active","Active"],["draft","Draft"],["inactive","Inactive"]]} />
        <Select label="Renewal" value={form.renewal} onChange={v => set("renewal", v)}
          options={[["manual","Manual"],["automatic","Automatic"]]} />
      </div>
      <div className="space-y-2 bg-slate-50 rounded-xl p-3">
        <Toggle label="Bulk Export" value={form.bulk_export} onChange={v => set("bulk_export", v)} />
        <Toggle label="Free Tier Plan" value={form.is_free_tier} onChange={v => set("is_free_tier", v)} />
      </div>
      <div>
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Features</label>
        <div className="flex gap-2 mb-2">
          <input value={featureInput} onChange={e => setFeatIn(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addFeature(); }}}
            placeholder="e.g. Priority support" className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:border-orange-400" />
          <button type="button" onClick={addFeature}
            className="px-3 py-2 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600">Add</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(form.features || []).map((f, i) => (
            <span key={i} className="bg-orange-100 text-orange-700 text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
              {f}
              <button type="button" onClick={() => set("features", form.features.filter((_,j)=>j!==i))}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel}
          className="flex-1 border-2 border-slate-200 text-slate-600 font-semibold py-2.5 rounded-xl hover:border-slate-400">
          Cancel
        </button>
        <button type="button" onClick={() => onSave(form)}
          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> Save Plan
        </button>
      </div>
    </div>
  );
}

function PaymentSettings({ token }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/subscription-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setForm(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/subscription-settings`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Payment settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50">
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-orange-500" />
          <span className="font-semibold text-slate-800">Payment Settings (Bank / UPI / QR)</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3 border-t border-slate-100">
          {loading ? <p className="text-sm text-slate-400 py-4">Loading…</p> : (
            <>
              <div className="grid grid-cols-2 gap-3 pt-3">
                {[
                  ["Bank Name",       "payment_bank_name"],
                  ["Account Name",    "payment_account_name"],
                  ["Account Number",  "payment_account_number"],
                  ["IFSC Code",       "payment_ifsc"],
                  ["Branch",          "payment_bank_branch"],
                  ["UPI ID",          "payment_upi_id"],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
                    <input value={form[key] || ""} onChange={e => set(key, e.target.value)}
                      className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:border-orange-400 bg-white" />
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">UPI QR Code Image</label>
                <MediaUploadInput
                  token={token} value={form.payment_qr_url || ""} mediaType="image"
                  onChange={url => set("payment_qr_url", url)}
                  label="Upload QR code image"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Payment Instructions (shown to customers)</label>
                <textarea value={form.payment_instructions || ""} onChange={e => set("payment_instructions", e.target.value)}
                  rows={3} className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:border-orange-400 resize-none" />
              </div>
              <button onClick={save} disabled={saving}
                className="bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-xl text-sm flex items-center gap-2">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function SubscriptionPlansAdmin() {
  const { token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // "new" | plan id | null
  const [editForm, setEditForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/subscription-plans`, { headers: { Authorization: `Bearer ${token}` } });
      setPlans(r.data);
    } catch { toast.error("Failed to load plans"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openNew  = () => { setEditForm({ ...BLANK_PLAN }); setEditingId("new"); };
  const openEdit = (p) => { setEditForm({ ...p }); setEditingId(p.id); };
  const cancel   = () => { setEditingId(null); setEditForm(null); };

  const save = async (form) => {
    try {
      if (editingId === "new") {
        await axios.post(`${API}/api/admin/subscription-plans`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Plan created");
      } else {
        await axios.put(`${API}/api/admin/subscription-plans/${editingId}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Plan updated");
      }
      cancel();
      load();
    } catch { toast.error("Failed to save"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete plan "${name}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/subscription-plans/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Plan deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const createFreePlan = async () => {
    if (!window.confirm("Create a default Free Plan (₹0, 1 download, lifetime access)?")) return;
    const freePlan = {
      name: "Free Plan", price: 0, currency: "INR",
      download_limit: 1, validity_days: 36500,
      features: ["1 product data download", "Excel format", "Complete product specs"],
      status: "active", visibility: "public", description: "Start free — download your first product data at no cost.",
    };
    try {
      await axios.post(`${API}/api/admin/subscription-plans`, freePlan, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Free plan created!");
      load();
    } catch { toast.error("Failed to create free plan"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-100 px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Subscription Plans</h1>
            <p className="text-xs text-slate-400">Configure plans, pricing and payment settings</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {plans.length === 0 && (
            <button onClick={createFreePlan} data-testid="create-free-plan-btn"
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors">
              + Free Plan
            </button>
          )}
          <button onClick={openNew} data-testid="create-plan-btn"
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors">
            <Plus className="w-4 h-4" /> New Plan
          </button>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <PaymentSettings token={token} />

        {/* Create / Edit form */}
        {editingId && editForm && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h2 className="text-sm font-bold text-slate-700 mb-4">
              {editingId === "new" ? "Create New Plan" : `Edit: ${editForm.name}`}
            </h2>
            <PlanForm initial={editForm} onSave={save} onCancel={cancel} />
          </div>
        )}

        {/* Plans list */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        ) : plans.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl flex flex-col items-center justify-center py-16 gap-3">
            <Crown className="w-10 h-10 text-slate-200" />
            <p className="text-sm text-slate-500">No plans yet. Create one to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {plans.map(p => (
              <div key={p.id} data-testid={`plan-row-${p.id}`}
                className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Crown className="w-4 h-4 text-orange-400" />
                      <h3 className="font-bold text-slate-800">{p.name}</h3>
                      {p.is_free_tier && (
                        <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">FREE TIER</span>
                      )}
                    </div>
                    <div className="text-xl font-black text-slate-900">
                      {Number(p.price) === 0 ? "Free" : `₹${Number(p.price).toLocaleString("en-IN")}`}
                      {Number(p.price) > 0 && (
                        <span className="text-xs font-normal text-slate-400 ml-1">/ {p.billing}</span>
                      )}
                    </div>
                  </div>
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${
                    p.status === "active"   ? "bg-green-100 text-green-700" :
                    p.status === "draft"    ? "bg-yellow-100 text-yellow-700" :
                    "bg-slate-100 text-slate-500"
                  }`}>{p.status}</span>
                </div>
                <div className="space-y-1 text-xs text-slate-500 mb-4">
                  <p>{p.validity_days} days · {p.download_limit === -1 ? "Unlimited" : p.download_limit} downloads</p>
                  <p>{p.data_access === "complete" ? "Complete data" : "Basic data"} · {p.billing}</p>
                  {p.features?.length > 0 && (
                    <p className="text-orange-600">{p.features.slice(0,2).join(", ")}{p.features.length > 2 ? ` +${p.features.length-2} more` : ""}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(p)}
                    className="flex-1 flex items-center justify-center gap-1.5 border border-slate-200 hover:border-orange-400 text-slate-600 hover:text-orange-600 py-2 rounded-xl text-xs font-semibold transition-colors">
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => del(p.id, p.name)}
                    className="flex items-center justify-center gap-1.5 border border-slate-200 hover:border-red-400 text-slate-400 hover:text-red-600 py-2 px-3 rounded-xl text-xs font-semibold transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
